import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewApi } from '@/lib/api';
import { queryKeys, regeneratingAwareInterval, invalidatePostLists } from './shared';

// Review
export const useReviewPosts = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.review(workspaceId),
    queryFn: () => reviewApi.list(workspaceId),
    enabled: !!workspaceId,
    // 60s baseline, but drops to 3s while any post is regenerating so the
    // "Regenerating…" card flips to the new copy without a manual refresh.
    refetchInterval: regeneratingAwareInterval(60000),
  });

export const useApprovePost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.approve(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};

export const useRejectPost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.reject(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};

export const useRegeneratePost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.regenerate(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};

export const usePublishNow = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.publishNow(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};

export const useRepublishPost = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.republish(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};

export const useGenerateVisual = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => reviewApi.generateVisual(workspaceId, postId),
    onSuccess: () => invalidatePostLists(qc, workspaceId),
  });
};
